import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDoctorProfile } from '@/hooks/doctor/useDoctorProfile';
import { useTelemedicineSession, useStartSession, useSessionParticipants } from '@/hooks/telemedicine';
import { Button } from '@/components/ui/Button';
import { ParticipantsList } from '@/components/telemedicine';

export function DoctorConsultationPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { data: profile } = useDoctorProfile(user?.id || '');

  const { data: session, refetch } = useTelemedicineSession(sessionId);
  const { data: participants = [] } = useSessionParticipants(sessionId);
  const { mutateAsync: startSession, isPending } = useStartSession();

  const isDoctor = Boolean(
    session && (
      user?.id === session.doctorId ||
      profile?.id === session.doctorId ||
      profile?.userId === session.doctorId ||
      user?.role === 'DOCTOR'
    )
  );

  useEffect(() => {
    if (!isDoctor || !session) return;
    if (session.status === 'CREATED') {
      startSession({
        sessionId: session.id,
        doctorId: profile?.id || user!.id,
      })
        .then(() => {
          refetch();
          navigate(`/medico/telemedicine/video/${session.id}`);
        })
        .catch(() => {
          navigate(`/medico/telemedicine/video/${session.id}`);
        });
    } else if (session.status === 'WAITING' || session.status === 'IN_PROGRESS') {
      navigate(`/medico/telemedicine/video/${session.id}`);
    }
  }, [session?.status, isDoctor, session?.id, profile?.id, user, navigate, refetch, startSession]);

  if (!session) {
    return (
      <div className="container py-12 text-center text-gray-500">
        Carregando...
      </div>
    );
  }

  if (!isDoctor) {
    return (
      <div className="container max-w-2xl py-12 text-center">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h1 className="text-xl font-bold text-red-900">Acesso negado</h1>
          <p className="text-red-700 mt-2">Apenas o médico atribuído pode iniciar esta consulta.</p>
          <Button onClick={() => navigate('/medico/dashboard')} className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Painel do Médico</h1>
        <p className="text-gray-500 mt-1">Controle pré-consulta</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <InfoCard label="Status" value={session.status} highlight />
          <InfoCard label="Sala" value={session.roomId?.substring(0, 12) || '—'} />
          <InfoCard label="Paciente" value={session.patientId.substring(0, 8)} />
        </div>

        <div>
          <h2 className="font-semibold text-gray-900 mb-2">Participantes</h2>
          <ParticipantsList participants={participants} localUserId={user?.id} />
        </div>

        <div className="flex gap-2">
          {session.status === 'CREATED' && (
            <Button onClick={() => startSession({ sessionId: session.id, doctorId: profile?.id || user!.id }).then(() => navigate(`/medico/telemedicine/video/${session.id}`))} disabled={isPending}>
              {isPending ? 'Iniciando...' : 'Iniciar Consulta'}
            </Button>
          )}
          {(session.status === 'WAITING' || session.status === 'IN_PROGRESS') && (
            <Button onClick={() => navigate(`/medico/telemedicine/video/${session.id}`)}>
              Entrar na Sala
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/medico/dashboard')}>
            Voltar
          </Button>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold mt-1 ${highlight ? 'text-blue-900' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}